import { useCallback, useMemo, useState } from 'react';
import classnames from 'classnames';
import { ToggleButton } from '@leverege/ui-elements';
import StringUtil from '@leverege/string-util';
import { motion, AnimatePresence } from 'motion/react';
import { Config } from '@leverege/plugin';
import { GlobalState, UI } from '@leverege/ui-redux';
import Path from '@leverege/path';
import { useSelector } from 'react-redux';
import { Tooltip } from 'react-tooltip';
import S from './LayerToggle.css';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_LAYER_SETTINGS_PATH = 'rsrc/overlays';
export default {
  id: 'rsrc.subRsrcLayerToggle',
  name: 'SubRsrc Layer Toggle',
  matches: {
    client: 'OverlayDetailRenderer',
    objectType: 'rsrc'
  },
  layout: {
    sort: 'aaa'
  },
  handles: ({
    layers
  }) => {
    return layers && layers.length > 0;
  },
  customComponent: SubRsrcLayerToggle
};
function SubRsrcLayerToggle({
  context
}) {
  const {
    layers
  } = context;
  const [hovered, setHovered] = useState(false);
  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);
  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);
  const layerSettingsPath = Config.get('OverlayImageRenderers', 'layerSettingsPath', DEFAULT_LAYER_SETTINGS_PATH);
  const settings = useSelector(state => UI.get(state, layerSettingsPath));
  const layerArr = useMemo(() => {
    const mapped = layers.map(layer => {
      return {
        name: layer.split('/').map(part => StringUtil.camelCaseToTitleCase(part)).join(' / '),
        key: layer
      };
    });
    mapped.unshift({
      name: 'All Layers',
      key: 'all'
    });
    return mapped;
  }, [layers]);
  const onChange = useCallback(({
    data,
    value
  }) => {
    if (data === 'all/visible') {
      const firstVal = UI.get(GlobalState.get(), `${layerSettingsPath}/${layerArr[0].key}/visible`);
      const setVal = firstVal === false;
      const changes = layerArr.reduce((prev, curr) => {
        prev[`${layerSettingsPath}/${curr.key}/visible`] = setVal;
        return prev;
      }, {});
      GlobalState.dispatch(UI.multiSet(changes));
    } else {
      GlobalState.dispatch(UI.set(`${layerSettingsPath}/${data}`, value));
    }
  }, [layerSettingsPath, layerArr]);
  const mapItems = useCallback(layer => {
    const value = Path.get(`${layer.key}/visible`, settings, true);
    return /*#__PURE__*/_jsx(MotionItem, {
      children: /*#__PURE__*/_jsx(ToggleButton, {
        "data-tooltip-id": "layerTooltip",
        "data-tooltip-content": `${layer.name}: ${value ? 'On' : 'Off'}`,
        variant: "layerToolbar",
        eventData: `${layer.key}/visible`,
        onChange: onChange,
        value: value,
        iconOn: "fa-solid fa-fw fa-eye",
        iconOff: "fa-solid fa-fw fa-eye-slash"
      }, layer.key)
    }, layer.key);
  }, [onChange, settings]);
  const menuItems = hovered ? layerArr.map(mapItems) : layerArr.slice(0, 1).map(mapItems);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Tooltip, {
      className: S.tooltip,
      place: "right",
      id: "layerTooltip"
    }), /*#__PURE__*/_jsx("div", {
      className: classnames(S.container, hovered && S.hovered),
      onMouseLeave: onMouseLeave,
      onMouseEnter: onMouseEnter,
      children: /*#__PURE__*/_jsx(AnimatePresence, {
        mode: "popLayout",
        children: menuItems
      })
    })]
  });
}
function MotionItem({
  children,
  fixedPosition
}) {
  return /*#__PURE__*/_jsx(motion.div, {
    className: classnames(S.node, fixedPosition && S.fixedPosition),
    layout: true,
    transition: {
      duration: 0.1
    },
    initial: {
      height: 0
    },
    animate: {
      height: 'auto'
    },
    exit: {
      height: 0
    },
    children: children
  });
}